import type { FormEvent, KeyboardEvent } from 'react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DIALOG_FIELD_AREA_CLASS,
  DialogField,
} from '@/components/ui/dialog-form'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { formatShortcutKeys } from '@/utils/keyboard-utils'
import { ChatCircleTextIcon } from '@phosphor-icons/react'

import { STATUS_COMMENT_MAX_LENGTH } from '@scrambl/shared'

export interface StatusCommentDialogProps {
  open: boolean
  statusName: string
  statusColor?: string | null
  /** Tasks the comment will be posted to; above 1 the copy says so. */
  taskCount?: number
  onSubmit: (comment: string) => void
  onCancel: () => void
}

/**
 * Asks for the comment a status requires before a task moves into it
 * (SD-145). Nothing is saved until the comment is submitted; cancel leaves
 * the task where it was. One dialog for every surface: pickers, board drops,
 * bulk actions and the API-layer fallback all open it through
 * StatusCommentPromptHost.
 */
export function StatusCommentDialog({
  open,
  statusName,
  statusColor,
  taskCount = 1,
  onSubmit,
  onCancel,
}: StatusCommentDialogProps) {
  const [comment, setComment] = useState('')
  const trimmed = comment.trim()
  const isMany = taskCount > 1

  const handleSubmit = (event?: FormEvent) => {
    event?.preventDefault()
    if (!trimmed) return
    onSubmit(trimmed)
    setComment('')
  }

  const handleCancel = () => {
    setComment('')
    onCancel()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const statusLabel = (
    <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground"
        style={statusColor ? { backgroundColor: statusColor } : undefined}
      />
      {statusName}
    </span>
  )

  return (
    <DialogPattern
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) handleCancel()
      }}
      title={`Move to ${statusName}`}
      description={
        <>
          {statusLabel} needs a comment.{' '}
          {isMany
            ? `It will be posted to each of the ${taskCount} tasks.`
            : "It will be posted to the task's comments."}
        </>
      }
      icon={<ChatCircleTextIcon className="h-4 w-4 shrink-0 text-primary" />}
      isForm
      onSubmit={handleSubmit}
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={!trimmed}
            className="min-w-[100px] text-sm"
          >
            {isMany ? `Move ${taskCount} tasks` : 'Move task'}
          </Button>
        </>
      }
    >
      <DialogField
        label="Comment"
        htmlFor="status-comment"
        required
        hint={`Press ${formatShortcutKeys(['Mod', 'Enter'])} to submit`}
      >
        <Textarea
          id="status-comment"
          autoFocus
          value={comment}
          maxLength={STATUS_COMMENT_MAX_LENGTH}
          onChange={event => setComment(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Why is ${isMany ? 'this work' : 'this task'} moving to ${statusName}?`}
          className={cn(DIALOG_FIELD_AREA_CLASS, 'min-h-[100px] resize-none')}
        />
      </DialogField>
    </DialogPattern>
  )
}
